import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check } from '@/shared/icons';
import { toast } from 'react-toastify';
import { Button } from '@/shared/shadcn/ui/Button';
import { NotificationEventItem } from '@/shared/types/commonTypes';
import { getNotificationModulesApi, markNotificationReadApi, markAllNotificationsReadApi } from '@/shared/api/sharedService';
import { useAppSelector } from '@/app/hooks';
import {
  formatDateWithPracticeTimeZone,
  formatRelativeToNowWithPracticeTimeZone,
} from '@/shared/utils/dateTimeWithPracticeZone';

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: NotificationEventItem[];
  onFilterChange?: (moduleId?: number) => void;
  onNotificationClick?: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ isOpen, onClose, notifications, onFilterChange, onNotificationClick }) => {
  const navigate = useNavigate();
  const auth = useAppSelector((state) => state.auth);
  const practiceTimeZone = auth.practice?.timeZone;
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const filterRef = useRef<HTMLDivElement>(null);
  const [filterOptions, setFilterOptions] = useState<Array<{
    id: string;
    label: string;
    icon: string;
    moduleIcon?: string;
    moduleId?: number;
  }>>([
    { id: 'all', label: 'All Notification', icon: 'bi-bell' },
  ]);
  const [isMarkAllReadLoading, setIsMarkAllReadLoading] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilterMenu(false);
      }
    };

    if (showFilterMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterMenu]);

  useEffect(() => {
    const userId = auth.user?.authUserId;
    if (!isOpen || !userId) return;

    const fetchModules = async () => {
      try {
        const response = await getNotificationModulesApi(userId);
        if (response.success && response.list && response.list.length > 0) {
          const apiFilters = response.list.map((item) => ({
            id: item.moduleCode.toLowerCase(),
            label: item.moduleName.trim(),
            icon: 'bi-chat-square',
            moduleIcon: item.moduleIcon,
            moduleId: item.id,
          }));

          setFilterOptions([
            { id: 'all', label: 'All Notification', icon: 'bi-bell' },
            ...apiFilters,
          ]);
        }
      } catch (error) {
        console.error('Error fetching notification modules:', error);
      }
    };

    fetchModules();
  }, [isOpen, auth.user?.authUserId]);

  const handleFilterSelect = (filterId: string, moduleId?: number) => {
    setActiveFilter(filterId);
    setShowFilterMenu(false);
    onFilterChange?.(filterId === 'all' ? undefined : moduleId);
  };

  const getFilterIconBg = (optionId: string) => {
    const id = optionId.toLowerCase();
    if (id === 'all') return 'bg-blue-50';
    if (id.includes('appt')) return 'bg-purple-100';
    if (id.includes('pay') || id.includes('membership')) return 'bg-green-100';
    if (id.includes('form')) return 'bg-amber-50';
    return 'bg-blue-50';
  };

  const formatTimestamp = (isoDate: string, timeZone?: string) => {
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays < 7) {
      const relative = formatRelativeToNowWithPracticeTimeZone(isoDate, timeZone);
      return relative || 'Just now';
    }

    return formatDateWithPracticeTimeZone(
      isoDate,
      timeZone,
      { year: 'numeric', month: 'numeric', day: 'numeric' },
      undefined
    );
  };

  const handleNotificationClick = async (notification: NotificationEventItem) => {
    const userId = auth.user?.authUserId;
    if (!userId) return;
    if (notification.readStatus) return;

    try {
      await markNotificationReadApi(notification.id, userId);
      onNotificationClick?.();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed sm:absolute right-0 sm:right-0 top-0 sm:top-full sm:mt-2 w-full sm:w-[380px] md:w-[400px] max-w-[480px] h-auto min-h-[300px] max-h-[90vh] bg-white rounded-t-2xl sm:rounded-2xl shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)] z-50 border-[0.667px] border-gray-200 overflow-visible flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg leading-[28px] font-semibold text-[#101828] ">Notifications</h3>
          <div className="flex items-center gap-2">
            {/* Filter */}
            <div className="relative" ref={filterRef}>
              <button
                className="bg-surface-subtle border border-surface-subtle rounded p-1.5 hover:bg-gray-200 transition-colors"
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                title="Filter notifications"
              >
                <i className="bi bi-filter text-base text-gray-600"></i>
              </button>

              {showFilterMenu && (
                <div className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 top-16 sm:top-full w-auto sm:w-[223px] bg-white rounded-lg shadow-[0px_4px_6px_0px_rgba(0,0,0,0.1)] z-50 border-[0.667px] border-gray-200 pt-2 px-2 pb-0.5">
                  {filterOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleFilterSelect(option.id, option.moduleId)}
                      className={`w-full flex items-center gap-3 h-[52px] px-3 rounded-md transition-colors text-left ${
                        activeFilter === option.id ? 'bg-gray-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      {option.moduleIcon ? (
                        <img src={option.moduleIcon} alt={option.label} className="flex-shrink-0 w-8 h-8 rounded-md object-contain" />
                      ) : (
                        <div className={`flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${getFilterIconBg(option.id)}`}>
                          <i className={`${option.icon} text-lg ${option.id === 'all' ? 'text-hh-primary-bright' : 'text-gray-600'}`}></i>
                        </div>
                      )}
                      <span className={`flex-1 text-base leading-[24px] whitespace-nowrap ${
                        activeFilter === option.id ? 'text-hh-primary-bright font-medium' : 'text-gray-900 font-normal'
                      }`}>
                        {option.label}
                      </span>
                      {activeFilter === option.id && (
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" strokeWidth={2.5} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Close */}
            <button
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Notification List */}
        <div className="flex-1 overflow-y-auto min-h-0 sm:max-h-[500px]">
          <div className="flex flex-col gap-1 p-0">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No notifications available
              </div>
            ) : (
              notifications.map((notification) => {
                const timestamp = formatTimestamp(notification.createdAt, practiceTimeZone);
                const isUnread = !notification.readStatus;

                return (
                  <div
                    key={notification.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNotificationClick(notification)}
                    onKeyDown={(e) => e.key === 'Enter' && handleNotificationClick(notification)}
                    className="bg-surface-subtle hover:bg-[#E7F1FF] transition-colors cursor-pointer lg-plus mx-0 flex flex-col gap-2 p-[13px]"
                  >
                    <div className="flex gap-3 items-start">
                      {/* Module Icon */}
                      {notification.moduleIcon && (
                        <img
                          src={notification.moduleIcon}
                          alt={notification.applicationModuleName}
                          className="flex-shrink-0 w-6 h-6 lg-plus object-contain mt-0.5"
                        />
                      )}

                      {/* Description */}
                      <p className={`flex-1 text-sm leading-[20px] ${
                        isUnread ? 'text-[#101828] font-medium' : 'text-[#364153] font-normal'
                      }`}>
                        {notification.notificationDescription}
                      </p>
                    </div>

                    {/* Timestamp */}
                    <div className="flex items-center justify-end gap-1">
                      {isUnread && (
                        <div className="w-2 h-2 rounded-full bg-hh-primary-bright" />
                      )}
                      <p className="text-xs leading-[16px] font-normal text-[#6a7282] ">
                        {timestamp}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="bg-gray-50 border-t-[0.667px] border-gray-200 flex items-center gap-2 px-4 pt-[17px] pb-4 rounded-b-[16px] flex-shrink-0">
            <Button
              variant="outline"
              disabled={isMarkAllReadLoading}
              className="flex-1 text-sm font-medium text-hh-primary border-[0.667px] border-hh-primary-pale bg-white hover:bg-hh-primary-light hover:text-hh-primary lg-plus leading-[20px] h-[37px]"
              onClick={async () => {
                const userId = auth.user?.authUserId;
                if (!userId) return;
                try {
                  if (isMarkAllReadLoading) return;
                  setIsMarkAllReadLoading(true);
                  const response = await markAllNotificationsReadApi(userId);
                  toast.success(response?.responseMsg || 'All notifications marked as read');
                  onNotificationClick?.();
                } catch (error) {
                  console.error('Error marking all as read:', error);
                  toast.error('Failed to mark all notifications as read');
                } finally {
                  setIsMarkAllReadLoading(false);
                }
              }}
            >
              {isMarkAllReadLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="bi bi-arrow-clockwise animate-spin" />
                  <span className="whitespace-nowrap">Marking...</span>
                </span>
              ) : (
                'Mark All Read'
              )}
            </Button>
            <Button
              variant="default"
              className="flex-1 text-sm font-medium lg-plus leading-[20px] h-[37px]"
              onClick={() => {
                navigate('/notifications');
                onClose();
              }}
            >
              View All
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationPanel;
